'use client';

import { c as _c } from "react/compiler-runtime";
import { useFrame, useThree } from '@react-three/fiber';
import { type ReactNode, useRef } from 'react';
import { type Group, Vector3 } from 'three';
import type { EngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { ENGINE_PLANE_Z } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/constants';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';

// Re-read every frame from the camera, so it must not allocate.
const PLANE_POSITION = new Vector3(0, 0, ENGINE_PLANE_Z);
export interface EngineStageProps {
  // WHERE THE ENGINE IS, in CSS px relative to the canvas box. Written by
  // whichever slide is active -- see useEngineAnchor.
  anchorRef: MutableRef<EngineAnchor>;
  // Everything welded to the engine: the aura, and later the flow's
  // obstacle sampling. All authored in engine-box units.
  children?: ReactNode;
}

/**
 * A group that sits exactly on the engine's on-screen rect.
 *
 * THE CANVAS DOES NOT DRAW THE ENGINE. The DOM `<Image>` is the engine the
 * user sees, and it keeps everything that makes it good: a responsive
 * `srcset` that fetches the right resolution for the screen, alt text, and
 * a picture that renders whether or not WebGPU works at all.
 *
 * What the canvas needs is the engine's ALPHA as DATA -- to cut the aura to
 * its silhouette, and later to deflect the airflow around it. That is a
 * mask, not a picture, so nothing here draws the artwork.
 *
 * WHAT THAT COSTS, stated plainly: the DOM image renders ABOVE the canvas,
 * so nothing drawn in here can ever appear in FRONT of the engine. A ribbon
 * can pass behind it and around it, never across it. That was the one
 * capability traded away for keeping the responsive image, and it is the
 * only thing the alternative would have bought.
 *
 * THE GROUP IS THE COORDINATE SPACE. It is positioned and scaled each frame
 * so its local -0.5..0.5 box lands on the measured rect, which means
 * children are authored in engine-box units where 1 is the engine's side --
 * the one space that survives both slides' very different responsive CSS
 * without anything being authored in world units.
 */
export const EngineStage = t0 => {
  const $ = _c(7);
  const {
    anchorRef,
    children
  } = t0;
  const size = useThree(_temp);
  const viewport = useThree(_temp2);
  const camera = useThree(_temp3);
  const groupRef = useRef(null);
  let t1;
  if ($[0] !== anchorRef.value || $[1] !== camera || $[2] !== size || $[3] !== viewport) {
    t1 = () => {
      const group = groupRef.current;
      const anchor = anchorRef.value;
      if (!group || !anchor.isMeasured || size.width <= 0 || size.height <= 0) {
        return;
      }
      const planeViewport = viewport.getCurrentViewport(camera, PLANE_POSITION);
      const worldPerPx = planeViewport.width / size.width;
      group.position.set((anchor.centerX - size.width / 2) * worldPerPx, -(anchor.centerY - size.height / 2) * worldPerPx, ENGINE_PLANE_Z);
      group.scale.setScalar(anchor.size * worldPerPx);
    };
    $[0] = anchorRef.value;
    $[1] = camera;
    $[2] = size;
    $[3] = viewport;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useFrame(t1);
  let t2;
  if ($[5] !== children) {
    t2 = <group ref={groupRef}>{children}</group>;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(state) {
  return state.size;
}
function _temp2(state_0) {
  return state_0.viewport;
}
function _temp3(state_1) {
  return state_1.camera;
}